
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  ReactNode,
  useEffect
} from 'react';
import axios, { AxiosInstance } from 'axios';
import { isTokenExpired, extractUserFromToken } from '../utils/tokenUtils';
import { useTokenRefresh } from '../hooks/useTokenRefresh';
import { useAxiosInterceptors } from '../hooks/useAxiosInterceptors';
import { AuthContextShape, User } from '../utils/types';

const VITE_API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || "/api";
const ACCESS_TOKEN_NAME = import.meta.env.VITE_AUTH_TOKEN_NAME || 'access_token';
const REFRESH_TOKEN_NAME = import.meta.env.VITE_REFRESH_TOKEN_NAME || 'refresh_token';

const AuthContext = createContext<AuthContextShape | undefined>(undefined);

export const useAuth = (): AuthContextShape => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  const apiRef = useRef<AxiosInstance>(
    axios.create({
      baseURL: VITE_API_ENDPOINT,
    })
  );
  const api = apiRef.current;

  const logout = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_NAME);
    localStorage.removeItem(REFRESH_TOKEN_NAME);
    setIsAuthenticated(false);
    setUser(null);
    clearRefreshTimer();
  }, []);

  const { refreshAccessToken, scheduleSilentRefresh, clearRefreshTimer } = useTokenRefresh({
    api,
    setUser,
    setIsAuthenticated,
    logout,
  });

  // Use our new interceptors hook
  useAxiosInterceptors(api, refreshAccessToken);

  const login = useCallback(
    (access: string, refresh: string, userOverride?: Partial<User>) => {
      localStorage.setItem(ACCESS_TOKEN_NAME, access);
      localStorage.setItem(REFRESH_TOKEN_NAME, refresh);
      api.defaults.headers.common['Authorization'] = `Bearer ${access}`;

      const decoded = extractUserFromToken(access);
      setUser({ ...decoded, ...userOverride } as User);
      setIsAuthenticated(true);

      scheduleSilentRefresh(access);
    },
    [api, scheduleSilentRefresh]
  );

  // Initialize auth state
  useEffect(() => {
    const access = localStorage.getItem(ACCESS_TOKEN_NAME);
    const refresh = localStorage.getItem(REFRESH_TOKEN_NAME);

    if (!access) {
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }

    if (!isTokenExpired(access)) {
      api.defaults.headers.common['Authorization'] = `Bearer ${access}`;
      setUser(extractUserFromToken(access));
      setIsAuthenticated(true);
      scheduleSilentRefresh(access);
      setIsLoading(false);
      return;
    }

    if (refresh) {
      refreshAccessToken(refresh)
        .catch(() => {})
        .finally(() => setIsLoading(false));
    } else {
      logout();
      setIsLoading(false);
    }
  }, [api, refreshAccessToken, scheduleSilentRefresh, logout]);

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    api,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
